import { PAYMENT_METHODS, type PaymentInput, type PaymentMethod } from "@/api/payments";
import { SelectField, TextField } from "@/components/ui/field";
import { formatMoney } from "@/lib/format";

/* The fields of a payment as typed, shared by the payment form and the receipt builder. */

export type PaymentValues = {
  received_on: string;
  amount: string;
  bank_charges: string;
  method: PaymentMethod;
  received_currency: string;
  received_amount: string;
  received_rate: string;
  received_by: string;
  received_place: string;
  reference: string;
  received_into_id: string;
  inr_credited: string;
  fx_rate: string;
  fira_ref: string;
  purpose_code: string;
  notes: string;
};

export type SetPaymentValue = <K extends keyof PaymentValues>(key: K, value: PaymentValues[K]) => void;

/** What the API is sent: trimmed, empty optional figures as null. */
export function paymentInput(values: PaymentValues): PaymentInput {
  return {
    received_on: values.received_on,
    amount: values.amount.trim(),
    bank_charges: values.bank_charges.trim() || "0",
    method: values.method,
    received_currency: values.received_currency.trim().toUpperCase(),
    received_amount: values.received_amount.trim() || null,
    received_rate: values.received_rate.trim() || null,
    received_by: values.received_by.trim(),
    received_place: values.received_place.trim(),
    reference: values.reference.trim(),
    received_into_id: values.received_into_id || null,
    inr_credited: values.inr_credited.trim() || null,
    fx_rate: values.fx_rate.trim() || null,
    fira_ref: values.fira_ref.trim(),
    purpose_code: values.purpose_code.trim().toUpperCase(),
    notes: values.notes.trim(),
  };
}

/** SAR 5,250 at 3.75 is USD 1,400.00: the sum the server checks the amount against. */
export function convertedAmount(values: PaymentValues): string | null {
  const rate = Number(values.received_rate);
  const amount = Number(values.received_amount);
  return amount > 0 && rate > 0 ? (amount / rate).toFixed(2) : null;
}

/** Method, and what was physically handed over when it is not the invoice currency. */
export function ArrivalFields({
  id,
  currency,
  values,
  set,
  errors,
  fixed = false,
  wholeSum = false,
}: {
  id: string;
  currency: string;
  values: PaymentValues;
  set: SetPaymentValue;
  errors: Record<string, string>;
  /** A recorded payment: how it arrived can no longer change. */
  fixed?: boolean;
  /** The money covers several invoices, so the amount received is the whole sum. */
  wholeSum?: boolean;
}) {
  const fixedHint = fixed ? "Fixed once recorded - void the payment to correct it" : undefined;
  const isCash = values.method === "cash";
  const converted = convertedAmount(values);
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        How it arrived
      </legend>
      <div className="grid gap-4 md:grid-cols-3">
        <SelectField
          id={`${id}-method`}
          label="Method"
          value={values.method}
          onChange={(value) => set("method", value as PaymentMethod)}
          options={PAYMENT_METHODS}
          error={errors.method}
          disabled={fixed}
          hint={fixedHint}
        />
        <TextField
          id={`${id}-received-currency`}
          label="Received in"
          value={values.received_currency}
          onChange={(value) => set("received_currency", value.toUpperCase())}
          error={errors.received_currency}
          disabled={fixed}
          maxLength={3}
          autoCapitalize="characters"
          hint={`Only if not ${currency}, e.g. SAR`}
        />
        <TextField
          id={`${id}-received-amount`}
          label="Amount received"
          inputMode="decimal"
          value={values.received_amount}
          onChange={(value) => set("received_amount", value)}
          error={errors.received_amount}
          disabled={fixed}
          hint={
            values.received_currency
              ? `In ${values.received_currency}${wholeSum ? ", the whole sum" : ""}`
              : undefined
          }
        />
        <TextField
          id={`${id}-received-rate`}
          label="Rate"
          inputMode="decimal"
          value={values.received_rate}
          onChange={(value) => set("received_rate", value)}
          error={errors.received_rate}
          disabled={fixed}
          hint={
            converted
              ? `= ${formatMoney(converted, currency)}`
              : `${values.received_currency || "FC"} per 1 ${currency}`
          }
        />
        {isCash && (
          <>
            <TextField
              id={`${id}-received-by`}
              label="Received by"
              value={values.received_by}
              onChange={(value) => set("received_by", value)}
              error={errors.received_by}
              disabled={fixed}
              hint="Who took the cash, on the company's behalf"
            />
            <TextField
              id={`${id}-received-place`}
              label="Place"
              value={values.received_place}
              onChange={(value) => set("received_place", value)}
              error={errors.received_place}
              disabled={fixed}
              hint="Where it changed hands"
            />
          </>
        )}
      </div>
      {isCash && (
        <p className="text-xs text-muted-foreground">
          Cash of ₹2,00,000 or more from one client in a day carries a penalty of the same amount
          (section 269ST). Deposit foreign currency through the bank promptly.
        </p>
      )}
    </fieldset>
  );
}
